import React, { useState, useEffect } from 'react';
import { batchApi } from '../../../api/batchApi';
import { BatchExecutionHistory, BatchJobType, STATUS_ICONS, STATUS_NAMES, STATUS_COLORS } from '../../../types/batch';
import { Button } from '../../ui/button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { BatchExecutionItemsList } from './BatchExecutionItemsList';
import { parseServerTime } from '@/lib/serverTime';

interface Props {
  jobType: BatchJobType;
  displayName: string;
  onClose: () => void;
}

export const BatchHistoryModal: React.FC<Props> = ({ jobType, displayName, onClose }) => {
  const [history, setHistory] = useState<BatchExecutionHistory[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<BatchExecutionHistory | null>(null);

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobType, page]);

  const fetchHistory = async () => {
    try {
      setIsLoading(true);
      const response = await batchApi.getBatchHistory(jobType, page, 20);
      
      if (response.data.success) {
        setHistory(response.data.data);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      }
    } catch (error) {
      console.error('이력 조회 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetail = async (historyId: number) => {
    try {
      const response = await batchApi.getBatchHistoryDetail(historyId);
      if (response.data.success) {
        setSelectedHistory(response.data.data);
      }
    } catch (error) {
      console.error('상세 조회 실패:', error);
      alert('상세 정보를 불러오는데 실패했습니다.');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'gray';
    const colorMap: Record<string, string> = {
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      blue: 'bg-blue-100 text-blue-800',
      orange: 'bg-orange-100 text-orange-800',
      gray: 'bg-gray-100 text-gray-800'
    };
    return colorMap[color];
  };

  const formatDuration = (durationMs: number | null): string => {
    if (!durationMs) return '-';
    if (durationMs < 1000) return `${durationMs}ms`;
    if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}초`;
    return `${Math.floor(durationMs / 60000)}분 ${Math.floor((durationMs % 60000) / 1000)}초`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold">📊 실행 이력</h2>
            <p className="text-gray-600 mt-1">{displayName} ({jobType})</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">로딩 중...</div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              실행 이력이 없습니다
            </div>
          ) : (
            <>
              {/* 통계 */}
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  총 {totalElements}건의 실행 이력 | 현재 페이지: {page + 1} / {totalPages}
                </p>
              </div>

              {/* 테이블 */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-3 text-left text-sm font-medium">실행 ID</th>
                      <th className="border p-3 text-left text-sm font-medium">상태</th>
                      <th className="border p-3 text-left text-sm font-medium">시작 시간</th>
                      <th className="border p-3 text-left text-sm font-medium">종료 시간</th>
                      <th className="border p-3 text-left text-sm font-medium">소요 시간</th>
                      <th className="border p-3 text-left text-sm font-medium">성공/실패</th>
                      <th className="border p-3 text-left text-sm font-medium">실행 구분</th>
                      <th className="border p-3 text-center text-sm font-medium">액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="border p-3 text-sm">#{item.id}</td>
                        <td className="border p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(item.status)}`}>
                            {STATUS_ICONS[item.status]} {STATUS_NAMES[item.status]}
                          </span>
                        </td>
                        <td className="border p-3 text-sm">
                          {parseServerTime(item.startedAt)?.toLocaleString('ko-KR')}
                        </td>
                        <td className="border p-3 text-sm">
                          {item.finishedAt ? parseServerTime(item.finishedAt)?.toLocaleString('ko-KR') : '-'}
                        </td>
                        <td className="border p-3 text-sm">
                          {formatDuration(item.durationMs)}
                        </td>
                        <td className="border p-3 text-sm">
                          <div className="flex gap-2">
                            <span className="text-green-600">✅ {item.successCount}</span>
                            <span className="text-red-600">❌ {item.failedCount}</span>
                          </div>
                        </td>
                        <td className="border p-3 text-sm">
                          {item.isManualExecution ? (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                              수동 실행
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">
                              스케줄
                            </span>
                          )}
                        </td>
                        <td className="border p-3 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetail(item.id)}
                          >
                            상세
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="mt-4 flex justify-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    이전
                  </Button>
                  <span className="px-4 py-2 text-sm">
                    {page + 1} / {totalPages}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages - 1}
                  >
                    다음
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* 상세 정보 모달 */}
        {selectedHistory && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold">실행 상세 정보</h3>
                  <button
                    onClick={() => setSelectedHistory(null)}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <span className="font-medium text-gray-600">실행 ID:</span>
                      <span className="ml-2">#{selectedHistory.id}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">상태:</span>
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${getStatusBadgeClass(selectedHistory.status)}`}>
                        {STATUS_ICONS[selectedHistory.status]} {STATUS_NAMES[selectedHistory.status]}
                      </span>
                    </div>
                  </div>

                  <div>
                    <span className="font-medium text-gray-600">시작 시간:</span>
                    <span className="ml-2">{parseServerTime(selectedHistory.startedAt)?.toLocaleString('ko-KR')}</span>
                  </div>

                  <div>
                    <span className="font-medium text-gray-600">종료 시간:</span>
                    <span className="ml-2">
                      {selectedHistory.finishedAt ? parseServerTime(selectedHistory.finishedAt)?.toLocaleString('ko-KR') : '-'}
                    </span>
                  </div>

                  <div>
                    <span className="font-medium text-gray-600">소요 시간:</span>
                    <span className="ml-2">{formatDuration(selectedHistory.durationMs)}</span>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <span className="font-medium text-gray-600">전체:</span>
                      <span className="ml-2">{selectedHistory.totalCount}건</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">성공:</span>
                      <span className="ml-2 text-green-600">{selectedHistory.successCount}건</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-600">실패:</span>
                      <span className="ml-2 text-red-600">{selectedHistory.failedCount}건</span>
                    </div>
                  </div>

                  {selectedHistory.resultMessage && (
                    <div>
                      <span className="font-medium text-gray-600">결과 메시지:</span>
                      <div className="mt-1 p-2 bg-blue-50 border border-blue-200 rounded">
                        {selectedHistory.resultMessage}
                      </div>
                    </div>
                  )}

                  {selectedHistory.errorMessage && (
                    <div>
                      <span className="font-medium text-gray-600">에러 메시지:</span>
                      <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-red-800">
                        {selectedHistory.errorMessage}
                      </div>
                    </div>
                  )}

                  {selectedHistory.errorStackTrace && (
                    <div>
                      <span className="font-medium text-gray-600">스택 트레이스:</span>
                      <div className="mt-1 p-2 bg-gray-100 border rounded text-xs font-mono overflow-x-auto max-h-40">
                        {selectedHistory.errorStackTrace}
                      </div>
                    </div>
                  )}

                  <div>
                    <span className="font-medium text-gray-600">실행 구분:</span>
                    <span className="ml-2">
                      {selectedHistory.isManualExecution ? '수동 실행' : '스케줄 실행'}
                    </span>
                  </div>

                  {selectedHistory.executedBy && (
                    <div>
                      <span className="font-medium text-gray-600">실행자:</span>
                      <span className="ml-2">{selectedHistory.executedBy}</span>
                    </div>
                  )}

                  <BatchExecutionItemsList historyId={selectedHistory.id} />
                </div>

                <div className="mt-6 flex justify-end">
                  <Button onClick={() => setSelectedHistory(null)}>닫기</Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
